import * as React from "react";
import { Mail } from "lucide-react";
import { useForm } from "react-hook-form";

import { useRequestAdminEmailChangeMutation } from "@/api/users-admin";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Userdata } from "@/types/user";
import { adminEmailChangeErrorMessage, type AdminEmailChangeFormData, type AdminEmailChangeStep } from "./types";

interface Props {
  user: Pick<Userdata, "id" | "email" | "canSelfServiceEmailChange">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusRef: React.RefObject<HTMLButtonElement | null>;
}

const defaultValues: AdminEmailChangeFormData = {
  newEmail: "",
};

export const AdminEmailChangeDialog = ({ user, open, onOpenChange, returnFocusRef }: Props) => {
  const [requestAdminEmailChange, { isLoading }] = useRequestAdminEmailChangeMutation();
  const [step, setStep] = React.useState<AdminEmailChangeStep>("request");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const submissionGenerationRef = React.useRef(0);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AdminEmailChangeFormData>({ defaultValues });

  const resetDialog = React.useCallback(() => {
    submissionGenerationRef.current += 1;
    reset(defaultValues);
    setStep("request");
    setErrorMessage(null);
  }, [reset]);

  React.useEffect(() => {
    if (!open) resetDialog();
  }, [open, resetDialog]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetDialog();
    onOpenChange(nextOpen);
  };

  const onSubmit = async (data: AdminEmailChangeFormData) => {
    const submissionGeneration = submissionGenerationRef.current;
    setErrorMessage(null);

    try {
      const result = await requestAdminEmailChange({
        userId: user.id,
        newEmail: data.newEmail,
      }).unwrap();
      if (submissionGeneration !== submissionGenerationRef.current) return;
      if (result.status === "pending") {
        reset({ newEmail: "" });
        setStep("pending");
      }
    } catch (error) {
      if (submissionGeneration !== submissionGenerationRef.current) return;
      setErrorMessage(adminEmailChangeErrorMessage(error));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-md overflow-y-auto"
        onCloseAutoFocus={event => {
          event.preventDefault();
          const opener = returnFocusRef.current;
          if (opener?.isConnected && !opener.disabled) opener.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Change email address</DialogTitle>
          <DialogDescription>
            {step === "request"
              ? "Start a confirmation process without changing the user's current email immediately."
              : "The user's current email stays active until confirmation is complete."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {step === "request" ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email-change-current-email">Current email address</Label>
                <Input
                  id="admin-email-change-current-email"
                  type="email"
                  value={user.email}
                  readOnly
                  spellCheck={false}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-email-change-new-email">New email address</Label>
                <Input
                  id="admin-email-change-new-email"
                  type="email"
                  autoComplete="email"
                  spellCheck={false}
                  aria-invalid={errors.newEmail ? "true" : "false"}
                  aria-describedby={errors.newEmail ? "admin-email-change-new-email-error" : undefined}
                  {...register("newEmail", {
                    required: "New email address is required.",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email address.",
                    },
                  })}
                />
                {errors.newEmail?.message ? (
                  <p id="admin-email-change-new-email-error" role="alert" className="text-sm text-destructive">
                    {errors.newEmail.message}
                  </p>
                ) : null}
              </div>

              <p className="text-sm text-muted-foreground">
                The current email remains active until the user confirms the new address.
              </p>

              {!user.canSelfServiceEmailChange ? (
                <Alert role="note">
                  <AlertDescription>
                    Update this user's email address in the identity provider as well. After the security window, a
                    later SSO sign-in may synchronize the identity provider address again.
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
          ) : null}

          {step === "pending" ? (
            <Alert role="status" variant="default">
              <Mail className="h-4 w-4" aria-hidden="true" />
              <h3 className="mb-1 font-medium leading-none tracking-tight">Confirmation process started</h3>
              <AlertDescription>
                Confirmation process started. If the address is eligible, the user can confirm it from the new inbox.
                The current email remains active until confirmation.
              </AlertDescription>
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="destructive">
              <h3 className="mb-1 font-medium leading-none tracking-tight">Request not completed</h3>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="gap-3 border-t pt-5 sm:space-x-0">
            {step === "pending" ? (
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Sending…" : "Send confirmation link"}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
